import React from "react";

// Mui
import Checkbox from "@mui/material/Checkbox";
import Fade from "@mui/material/Fade";
import Grid from "@mui/material/Grid2";
import TextField from "@mui/material/TextField";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

// Mui Styles
import DailySalesFooter from "@/components/DataTable/dailySalesFooter";
import { StyledPaper } from "@/styles/mui/globalStyles";
import { TableListTheme } from "@/styles/mui/tableListTheme";
import DownloadIcon from "@mui/icons-material/Download";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import IconButton from "@mui/material/IconButton";
import { createTheme, ThemeProvider } from "@mui/material/styles";

// Navigate
import { useNavigate } from "react-router-dom";

// Components
import Layout from "@/components/layout";
import Title from "@/components/title";
// import StaffCard from "@/components/staffCard";
import DataTable from "@/components/dataTable";

// Fetch
import { exportSalesSummary } from "@/fetch/fetchProduct";
import { getAllShopSalesTagetTotal } from "@/fetch/fetchTarget";
import { useCurrentAction } from "@/hooks/user";

// Moment
import { setSelectedDate } from "@/features/dailyManageSlice";
import moment from "moment";
import "moment/locale/zh-hk";
import { useDispatch, useSelector } from "react-redux";
// Config
import { DailyTableColumn } from "@/config/tableColumnConfig";
import _ from "lodash";
const DailyPage = (props) => {
  const { open, setOpen } = props;
  const actions = useCurrentAction();
  // Theme
  const theme = createTheme({
    components: {
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 16,
          },
        },
      },
    },
  });

  // Navigate
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const selectedDate = useSelector((state) => state.daily.selectedDate);
  const [isDownloading, setIsDownloading] = React.useState(false);
  const [snackMessage, setSnackMessage] = React.useState("");
  const [snackOpen, setSnackOpen] = React.useState(false);
  const [snackSeverity, setSnackSeverity] = React.useState("info");
  const [showZero, setShowZero] = React.useState(false);
  const [dataTableLoading, setDataTableLoading] = React.useState(false);
  const [allShopTargetSalesTotal, setAllShopTargetSalesTotal] = React.useState(
    []
  );
  const exportSalesReport = async () => {
    const startDate = moment(selectedDate.startDate).format("YYYY-MM-DD");
    const endDate = moment(selectedDate.endDate).format("YYYY-MM-DD");
    setIsDownloading(true);
    setSnackMessage("下載中...");
    setSnackSeverity("info");
    setSnackOpen(true);
    try {
      await exportSalesSummary(startDate, endDate).then((response) => {
        const url = window.URL.createObjectURL(new Blob([response]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `Daily Sales (${startDate}-${endDate}).xlsx`
        );
        document.body.appendChild(link);
        link.click();
        setSnackMessage("下載成功");
        setSnackSeverity("success");
      });
    } catch (error) {
      setSnackMessage("下載失敗");
      setSnackSeverity("error");
    } finally {
      setIsDownloading(false);
    }
  };

  const onCellClick = (data) => {
    // const dataCombie = { ...data, ...workData };
    // passing user_id & workdata
    navigate(`detail/${data.row.id}/${data.row.target_datetime}`);
  };

  const getRowId = (row) => {
    return row.internalId;
  };

  //Fetch all
  const fetchAll = async () => {
    try {
      setDataTableLoading(true);
      await getAllShopSalesTagetTotal({
        startDate: moment(selectedDate.startDate).format("YYYY-MM-DD"),
        endDate: moment(selectedDate.endDate).format("YYYY-MM-DD"),
      }).then((data) => {
        if (data && data.results) {
          let tempArr = [...data.data];
          for (let i = 0; i < tempArr.length; i++) {
            tempArr[i].work_total_amount =
              tempArr[i].work_total_amount !== null
                ? tempArr[i].work_total_amount
                : parseInt(0);
            tempArr[i].target_total_amount = parseInt(
              tempArr[i].target_total_amount
            );
            tempArr[i].sales_vs_target =
              (tempArr[i].work_total_amount / tempArr[i].target_total_amount) *
              100;
            tempArr[
              i
            ].combine_name = `${tempArr[i].work_name}(${tempArr[i].to_char})(${tempArr[i].work_shortcode})`;
            tempArr[i].internalId = i;
          }
          if (showZero) {
            setAllShopTargetSalesTotal(tempArr);
          } else {
            setAllShopTargetSalesTotal(
              _.filter(tempArr, (t) => t.work_total_amount !== "0")
            );
          }
        } else {
          setAllShopTargetSalesTotal([]);
        }
      });
    } catch (error) {
      console.log("error", error);
    } finally {
      setDataTableLoading(false);
    }
  };
  React.useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, showZero]);

  moment.updateLocale("zh-hk", {
    weekdaysShort: ["日", "一", "二", "三", "四", "五", "六"],
  });

  return (
    <Layout
      header="DailyPage"
      selectedIndex={3}
      snackOpen={snackOpen}
      snackClose={() => setSnackOpen(false)}
      snackMessage={snackMessage}
      snackSeverity={snackSeverity}
      open={open}
      setOpen={setOpen}
    >
      <Title
        title="銷售追蹤"
        children={
          actions?.download ? (
            <IconButton
              component="label"
              disabled={isDownloading}
              onClick={() => exportSalesReport()}
            >
              <DownloadIcon />
            </IconButton>
          ) : null
        }
      />
      <StyledPaper page={"yes"} alignItems="left">
        <ThemeProvider theme={theme}>
          <Grid container spacing={2}>
            <Grid
              size={{
                xs: 12,
                md: 5,
                lg: 5,
              }}
            >
              <DatePicker
                label="開始月份"
                value={selectedDate.startDate}
                onChange={(newValue) =>
                  dispatch(
                    setSelectedDate({
                      key: "startDate",
                      value: moment(newValue).format("YYYY-MM-DD"),
                    })
                  )
                }
                renderInput={(params) => (
                  <TextField {...params} sx={{ width: "100%" }} />
                )}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 5,
                lg: 5,
              }}
            >
              <DatePicker
                minDate={selectedDate.startDate}
                label="結束月份"
                value={selectedDate.endDate}
                onChange={
                  (newValue) =>
                    dispatch(
                      setSelectedDate({
                        key: "endDate",
                        value: moment(newValue).format("YYYY-MM-DD"),
                      })
                    )

                  //setSalesReportEndDate(newValue.endOf("month"))
                }
                renderInput={(params) => (
                  <TextField {...params} sx={{ width: "100%" }} />
                )}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 2,
                lg: 2,
              }}
            >
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      value={showZero}
                      onChange={(event) => {
                        setShowZero(event.target.checked);
                      }}
                    />
                  }
                  label="顯示＄0"
                />
              </FormGroup>
            </Grid>
          </Grid>
        </ThemeProvider>
      </StyledPaper>
      <Fade in={true} mountOnEnter unmountOnExit>
        <StyledPaper page={"yes"}>
          <ThemeProvider theme={TableListTheme}>
            <DataTable
              loading={dataTableLoading}
              rows={allShopTargetSalesTotal}
              columns={DailyTableColumn}
              Footer={DailySalesFooter}
              checkboxSelection={false}
              pageSize={25}
              onCellClick={(data) => onCellClick(data)}
              getRowId={getRowId}
              initialState={{
                sorting: {
                  sortModel: [{ field: "work_total_amount", sort: "desc" }],
                },
              }}
            />
          </ThemeProvider>
        </StyledPaper>
      </Fade>
    </Layout>
  );
};

export default DailyPage;
